import type { Repo } from '../data'
import { claimGame, fetchGameEvents, fetchScoringState, fromServerEvent } from '../data/live'
import type { SnapshotGame } from '../data/snapshotTypes'
import type { Game, ID, Player, StatEvent } from '../domain/types'

/**
 * Picking up a game somebody else started, on a device that has never seen it.
 *
 * This is the phone-in-the-stands case: the iPad's battery died at half time and
 * someone else carries on. The device needs four things before it can draw the
 * grid, and all four come from the server rather than being guessed:
 *
 *   1. the claim, taken atomically so two people tapping at once cannot both
 *      believe they have it;
 *   2. the roster the first scorer ticked, in their order -- without it the grid
 *      would show only players who have already done something;
 *   3. every event so far, so the totals continue rather than restart;
 *   4. local player rows for any fill-ins, who exist nowhere but on the
 *      original device.
 *
 * Order matters: the claim is taken first. Pulling the history and then failing
 * to get the pen would leave a device looking like it was scoring when it was
 * not, which is the kind of quiet wrongness this project keeps paying for.
 */
export interface TakeOverResult {
  ok: boolean
  error?: string
  gameId?: ID
}

export async function takeOverGame(args: {
  repo: Repo
  serverGameId: string
  fixture: SnapshotGame | undefined
  seasonId: ID
  players: Player[]
  existingGames: Game[]
}): Promise<TakeOverResult> {
  const { repo, serverGameId, fixture, seasonId, players, existingGames } = args

  const claim = await claimGame(serverGameId, true, 'takeover')
  if (!claim.ok) return { ok: false, error: claim.error }
  if (!claim.data.mine) {
    return { ok: false, error: `${claim.data.holder_name ?? 'Someone else'} still holds this game.` }
  }

  const [state, events] = await Promise.all([fetchScoringState(serverGameId), fetchGameEvents(serverGameId)])
  if (!events.ok) return { ok: false, error: events.error }

  const published = state.ok && state.data ? state.data : null
  const now = Date.now()

  // Fill-ins have no players row anywhere but on the device that created them.
  // Recreate them here under the SAME id the other device used, which is what
  // keeps one person's taps together rather than scattering them.
  const rosterEntries = published?.roster ?? []
  for (const entry of rosterEntries) {
    if (!entry.isFillIn) continue
    if (players.some((p) => p.id === entry.id)) continue
    const filler: Player = {
      id: entry.id,
      name: entry.name,
      active: true,
      isFillIn: true,
      fillInForGameId: serverGameId,
      createdAt: now,
      updatedAt: now,
    }
    await repo.savePlayer(filler)
  }

  const roster: ID[] = rosterEntries.length > 0
    ? rosterEntries.map((r) => r.id)
    // No published grid: fall back to whoever appears in the events, so at least
    // the totals are right even if the row order is not the scorer's.
    : [...new Set(events.data.map((e) => e.player_id ?? e.client_player_id).filter(Boolean) as string[])]

  const existing = existingGames.find((g) => g.id === serverGameId)
  const game: Game = existing
    ? { ...existing, roster, currentPeriod: published?.current_period ?? existing.currentPeriod, updatedAt: now }
    : {
        id: serverGameId,
        seasonId,
        date: fixture?.date ?? new Date().toISOString().slice(0, 10),
        playhqGameId: fixture?.playhq_game_id ?? null,
        opponentId: null,
        opponentName: fixture?.opponent_name ?? published?.opponent_name ?? 'Opponent',
        roundName: fixture?.round_name ?? null,
        roundAbbreviation: fixture?.round_abbreviation ?? null,
        venueName: fixture?.venue_name ?? null,
        courtName: fixture?.court_name ?? null,
        isFinalRound: fixture?.is_final_round,
        countsTowardLadder: fixture?.counts_toward_ladder,
        homeAway: (fixture?.home_away ?? (published?.home_away as 'home' | 'away' | undefined) ?? 'home'),
        roster,
        ourScore: null,
        theirScore: null,
        status: 'in_progress',
        currentPeriod: published?.current_period ?? 1,
        notes: '',
        createdAt: now,
        updatedAt: now,
      }

  await repo.saveGame(game)

  const local: StatEvent[] = events.data.map(fromServerEvent)
  if (local.length > 0) await repo.saveEvents(local)

  return { ok: true, gameId: game.id }
}
